import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import ProductGridCard from "@/components/ProductGridCard";

/**
 * Grid view — masonry-style product browsing.
 * Fetches same /feed API as doomscroll, renders as columns.
 */
export default function Grid({ filters, onOpenIntent }) {
  const sentinelRef = useRef(null);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, isError, refetch } = useInfiniteQuery({
    queryKey: ["grid", filters],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const params = { page: pageParam, page_size: 16, ...filters };
      Object.keys(params).forEach(
        (k) => (params[k] === "" || params[k] == null || (k === "kategori" && params[k] === "Semua")) && delete params[k]
      );
      const r = await api.get("/feed", { params });
      return r.data;
    },
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      });
    }, { rootMargin: "300px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = data?.pages.flatMap((p) => p.items) || [];

  // Error state — jangan tampilkan empty state saat gagal
  if (isError) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <div className="text-5xl mb-3">😵</div>
          <h3 className="font-serif italic text-2xl text-plum dark:text-cream">Gagal memuat</h3>
          <p className="text-plum/60 dark:text-cream/60 text-sm mt-1 mb-4">Cek koneksi internet kamu.</p>
          <button
            onClick={() => refetch()}
            className="h-11 px-6 rounded-full bg-sakura-400 text-plum font-semibold text-[11px] uppercase tracking-[0.2em] active:scale-95 transition-transform"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 p-4 pt-28">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="break-inside-avoid mb-3 rounded-3xl overflow-hidden bg-card border border-plum/10 shadow-dreamy">
            <div className="aspect-[4/5] shimmer-bg animate-shimmer" />
            <div className="p-3.5">
              <div className="h-3 w-20 rounded-full shimmer-bg animate-shimmer mb-2" />
              <div className="h-5 w-full rounded-full shimmer-bg animate-shimmer mb-2" />
              <div className="h-9 w-full rounded-full shimmer-bg animate-shimmer" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!items.length) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <div className="text-5xl mb-3">✨</div>
          <h3 className="font-serif italic text-2xl text-plum dark:text-cream">Belum ada barang</h3>
          <p className="text-plum/60 dark:text-cream/60 text-sm mt-1">Coba reset filter di atas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pt-28 pb-32">
      <div className="flex gap-3 items-start">
        {[0, 1].map((col) => (
          <div key={col} className="flex-1 min-w-0 flex flex-col">
            {items.filter((_, i) => i % 2 === col).map((p) => (
              <ProductGridCard key={p.id} product={p} onOpenIntent={onOpenIntent} />
            ))}
          </div>
        ))}
      </div>
      <div ref={sentinelRef} className="h-1 w-full" />
      {isFetchingNextPage && (
        <div className="mt-6 text-center text-xs text-plum/50 dark:text-cream/50 animate-pulse uppercase tracking-[0.2em]">
          Muat lebih banyak
        </div>
      )}
    </div>
  );
}
